import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock,
  User,
  Calendar,
  CalendarCheck,
  Share,
  X,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ListBullets,
  CaretDown,
  Copy,
  Check,
  FilePdf,
} from '@phosphor-icons/react';

const PDF_ENABLED_SLUGS = new Set(['montessori-approach-early-childhood-2026']);

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Post-process the article HTML:
 *   • Adds anchor ids to every H2/H3 → used by the TOC
 *   • Wraps <table>, <img>, and blockquote so they inherit the design system
 *   • Returns { html, toc:[{id,text,level}] }
 */
const enrichBody = (raw) => {
  if (!raw) return { html: '', toc: [] };
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
  const root = doc.body.firstChild;
  const toc = [];
  let counter = 0;

  root.querySelectorAll('h2, h3').forEach((h) => {
    counter += 1;
    const level = h.tagName === 'H2' ? 2 : 3;
    const text = h.textContent.trim();
    const id = `s-${counter}-${text.replace(/\s+/g, '-').slice(0, 40)}`;
    h.setAttribute('id', id);
    toc.push({ id, text, level });
  });

  // Wrap tables for horizontal scroll on mobile
  root.querySelectorAll('table').forEach((t) => {
    const wrap = doc.createElement('div');
    wrap.setAttribute('class', 'kc-table-wrap');
    t.parentNode.insertBefore(wrap, t);
    wrap.appendChild(t);
  });

  return { html: root.innerHTML, toc };
};

const KnowledgeArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0, images: [] });
  const bodyRef = useRef(null);

  // Fetch article + related
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/knowledge/articles/${slug}`);
        if (r.status === 404) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        setArticle(data);

        // Fetch related — either explicit slugs or same-category latest 3
        let rel = [];
        if (Array.isArray(data.related_slugs) && data.related_slugs.length) {
          const results = await Promise.all(
            data.related_slugs
              .filter((s) => s && s !== data.slug)
              .slice(0, 3)
              .map((s) =>
                fetch(`${API}/api/knowledge/articles/${s}`).then((x) =>
                  x.ok ? x.json() : null
                )
              )
          );
          rel = results.filter(Boolean).filter((a) => a.slug !== data.slug);
        }
        if (rel.length === 0 && data.category_slug) {
          const listRes = await fetch(
            `${API}/api/knowledge/articles?category=${encodeURIComponent(
              data.category_slug
            )}&page_size=4`
          ).then((x) => x.json());
          rel = (listRes.items || [])
            .filter((a) => a.slug !== data.slug)
            .slice(0, 3);
        }
        if (!cancelled) setRelated(rel);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [article]);

  const { html, toc } = useMemo(
    () => enrichBody(article?.body_html || ''),
    [article]
  );

  // Collect all article images + attach click handler for the lightbox
  // via event delegation (works reliably with dangerouslySetInnerHTML)
  useEffect(() => {
    if (!article || !bodyRef.current) return;
    const bodyEl = bodyRef.current;

    const collect = () =>
      Array.from(bodyEl.querySelectorAll('.kc-prose img')).map((img) => ({
        el: img,
        src: img.getAttribute('src'),
        alt: img.getAttribute('alt') || '',
        caption:
          img.closest('figure')?.querySelector('figcaption')?.innerText?.trim() ||
          '',
      }));

    const onClick = (e) => {
      const img = e.target.closest('.kc-prose img');
      if (!img) return;
      const list = collect();
      const idx = list.findIndex((x) => x.el === img);
      if (idx === -1) return;
      e.preventDefault();
      // Strip DOM refs from payload
      setLightbox({
        open: true,
        index: idx,
        images: list.map(({ src, alt, caption }) => ({ src, alt, caption })),
      });
    };
    bodyEl.addEventListener('click', onClick);
    return () => bodyEl.removeEventListener('click', onClick);
  }, [article, html]);

  const closeLightbox = useCallback(
    () => setLightbox((s) => ({ ...s, open: false })),
    []
  );
  const nextLightbox = useCallback(
    () =>
      setLightbox((s) =>
        s.images.length
          ? { ...s, index: (s.index + 1) % s.images.length }
          : s
      ),
    []
  );
  const prevLightbox = useCallback(
    () =>
      setLightbox((s) =>
        s.images.length
          ? {
              ...s,
              index: (s.index - 1 + s.images.length) % s.images.length,
            }
          : s
      ),
    []
  );

  // Keyboard navigation + body scroll lock while lightbox open
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') nextLightbox(); // RTL: left = next
      else if (e.key === 'ArrowRight') prevLightbox();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox.open, closeLightbox, nextLightbox, prevLightbox]);

  const openHeroLightbox = () => {
    if (!article?.hero_image_url) return;
    // Prepend hero to the currently discovered body images (if any)
    setLightbox((s) => {
      const hero = {
        src: article.hero_image_url,
        alt: article.title,
        caption: '',
      };
      const images =
        s.images && s.images.length
          ? [hero, ...s.images.filter((i) => i.src !== hero.src)]
          : [hero];
      return { open: true, index: 0, images };
    });
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no-op */
    }
  };

  if (notFound) {
    return (
      <main
        dir="rtl"
        className="min-h-screen bg-[#FAFAF7] pt-40 pb-20 text-center px-4"
      >
        <BookOpen size={48} weight="duotone" className="mx-auto text-[#B08A50] mb-6" />
        <h1 className="text-2xl font-bold text-[#1F3D2B] mb-4">المقال غير موجود</h1>
        <p className="text-[#6B7565] mb-8">قد يكون الرابط قديماً أو تمّت إزالته.</p>
        <Link
          to="/knowledge"
          className="inline-flex items-center gap-2 bg-[#1F3D2B] text-white rounded-xl px-6 py-3 text-sm font-bold"
        >
          العودة إلى مركز المعرفة
          <ArrowLeft size={14} className="rtl-flip" />
        </Link>
      </main>
    );
  }

  if (!article) {
    return (
      <main dir="rtl" className="min-h-screen bg-[#FAFAF7] pt-40 text-center">
        <p className="text-[#7A866E]">جارٍ تحميل المقال...</p>
      </main>
    );
  }

  return (
    <main
      dir="rtl"
      data-testid="knowledge-article-page"
      className="bg-[#FAFAF7] min-h-screen"
    >
      {/* Reading progress bar */}
      <div className="fixed top-0 inset-x-0 h-[3px] bg-transparent z-40 pointer-events-none">
        <div
          data-testid="kc-progress-bar"
          className="h-full bg-[#B08A50] transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-32 sm:pt-36 pb-10 bg-[#F5F2EC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/knowledge"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#4A5D23] hover:text-[#1F3D2B] mb-6 transition-colors"
          >
            <ArrowLeft size={13} className="rtl-flip" />
            مركز المعرفة
          </Link>
          {article.category_name_ar && (
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B6F3E] mb-4">
              {article.category_name_ar}
            </p>
          )}
          <h1
            data-testid="kc-article-title"
            className="font-['Noto_Kufi_Arabic'] font-extrabold text-[#1F3D2B] text-[28px] sm:text-[36px] md:text-[42px] leading-[1.3] tracking-[-0.005em] mb-6"
          >
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="text-[#3D512D] text-[16px] leading-9 mb-6">
              {article.excerpt}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-[#7A866E]">
            <span className="flex items-center gap-1.5">
              <User size={13} /> {article.author_name}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={13} /> نُشر: {fmt(article.published_at)}
            </span>
            {article.updated_at &&
              article.updated_at !== article.published_at && (
                <span className="flex items-center gap-1.5">
                  <CalendarCheck size={13} /> آخر تحديث: {fmt(article.updated_at)}
                </span>
              )}
            <span className="flex items-center gap-1.5">
              <Clock size={13} /> {article.reading_time_min} دقيقة قراءة
            </span>
          </div>

          {PDF_ENABLED_SLUGS.has(article.slug) && (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/articles/${article.slug}/pdf`}
                data-testid="kc-download-pdf"
                className="group inline-flex items-center gap-2.5 rounded-full border border-[#B08A50] bg-gradient-to-b from-[#FDFBF6] to-[#F5F2EC] px-5 py-2.5 text-[13px] font-semibold text-[#1F3D2B] shadow-sm transition-all hover:from-[#1F3D2B] hover:to-[#3D512D] hover:text-white hover:shadow-md hover:-translate-y-0.5"
              >
                <FilePdf
                  size={16}
                  weight="duotone"
                  className="text-[#B08A50] group-hover:text-[#F5E6C4] transition-colors"
                />
                تحميل PDF مصمَّم
                <span className="text-[10.5px] font-normal opacity-70 border-r border-[#8B6F3E] group-hover:border-white/40 pr-2.5 mr-1">
                  نسخة قراءة نصّية للطباعة
                </span>
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Hero image */}
      {article.hero_image_url && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-2">
          <img
            src={article.hero_image_url}
            alt={article.title}
            onClick={openHeroLightbox}
            data-testid="kc-hero-image"
            className="kc-hero-image w-full aspect-[16/9] object-cover rounded-[20px] shadow-sm"
          />
        </div>
      )}

      {/* Author card — universal template element */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-8">
        <div className="kc-author-card" data-testid="kc-author-card">
          <div className="kc-author-avatar">
            {(article.author_name || '؟').trim().charAt(0)}
          </div>
          <div className="kc-author-info">
            <h5>{article.author_name}</h5>
            <p>محتوى مرجعي — مركز المعرفة · آفاق الطفولة</p>
          </div>
        </div>
      </div>

      {/* Body + sidebar */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-[1fr_260px] gap-10">
        {/* Article body */}
        <article ref={bodyRef} className="min-w-0">
          <div
            data-testid="kc-article-body"
            className="kc-prose"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {/* References */}
          {Array.isArray(article.references) && article.references.length > 0 && (
            <section className="mt-14 pt-8 border-t border-[#E6E0D6]">
              <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-lg mb-4">
                المراجع
              </h3>
              <ol className="list-decimal pr-6 space-y-2 text-sm text-[#3D512D] leading-8">
                {article.references.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ol>
            </section>
          )}

          {/* FAQ */}
          {Array.isArray(article.faqs) && article.faqs.length > 0 && (
            <section className="mt-14 pt-8 border-t border-[#E6E0D6]" data-testid="kc-article-faq">
              <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-lg mb-5">
                الأسئلة الشائعة
              </h3>
              <div className="space-y-3">
                {article.faqs.map((f, i) => (
                  <FaqItem key={i} q={f.question} a={f.answer} />
                ))}
              </div>
            </section>
          )}

          {/* CTA box */}
          <section
            data-testid="kc-article-cta"
            className="mt-14 rounded-[24px] bg-[#1F3D2B] text-white p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#B08A50]/10 blur-3xl pointer-events-none" />
            <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-white text-xl sm:text-2xl mb-3">
              {article.cta_title || 'هل تحتاج إلى دعم أو تدريب متخصص؟'}
            </h3>
            {article.cta_body && (
              <p className="text-[#D8E0C9] leading-8 text-[15px] mb-6 max-w-2xl">
                {article.cta_body}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link
                to="/quotation"
                data-testid="kc-cta-consult-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-6 py-3 text-sm font-bold transition-colors"
              >
                اطلب استشارة
                <ArrowLeft size={14} className="rtl-flip" />
              </Link>
              <Link
                to="/contact"
                data-testid="kc-cta-contact-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white/10 rounded-xl px-6 py-3 text-sm font-bold transition-colors"
              >
                تواصل معنا
              </Link>
            </div>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-14 pt-8 border-t border-[#E6E0D6]" data-testid="kc-article-related">
              <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-lg mb-5">
                مقالات ذات صلة
              </h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/knowledge/${r.slug}`}
                    className="group bg-white rounded-[16px] border border-[#E6E0D6] overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-[4/3] bg-[#EEE8DC] overflow-hidden">
                      {r.hero_image_url ? (
                        <img
                          src={r.hero_image_url}
                          alt={r.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#B08A50]">
                          <BookOpen size={28} weight="duotone" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      {r.category_name_ar && (
                        <p className="text-[10px] text-[#4A5D23] mb-1.5">
                          {r.category_name_ar}
                        </p>
                      )}
                      <h4 className="font-bold text-[#1F3D2B] text-sm leading-snug">
                        {r.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Share */}
          <section className="mt-14 pt-8 border-t border-[#E6E0D6] flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#3D512D] font-bold">مشاركة المقال:</span>
            <ShareButton
              label="تويتر / X"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                article.title
              )}&url=${encodeURIComponent(shareUrl)}`}
              testid="kc-share-x"
            />
            <ShareButton
              label="واتساب"
              href={`https://wa.me/?text=${encodeURIComponent(
                article.title + '\n' + shareUrl
              )}`}
              testid="kc-share-whatsapp"
            />
            <ShareButton
              label="لينكدإن"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                shareUrl
              )}`}
              testid="kc-share-linkedin"
            />
            <button
              type="button"
              data-testid="kc-share-copy"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white rounded-full px-4 py-1.5 text-[12px] transition-colors"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'تم النسخ' : 'نسخ الرابط'}
            </button>
            <button
              type="button"
              data-testid="kc-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 border border-[#E6E0D6] text-[#3D512D] hover:border-[#1F3D2B] hover:text-[#1F3D2B] rounded-full px-4 py-1.5 text-[12px] transition-colors"
            >
              طباعة
            </button>
          </section>
        </article>

        {/* TOC (sticky on lg+) */}
        <aside className="hidden lg:block">
          {toc.length > 0 && (
            <div
              data-testid="kc-toc"
              className="sticky top-28 bg-white border border-[#E6E0D6] rounded-[16px] p-5"
            >
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-[#8B6F3E] font-medium mb-3 flex items-center gap-1.5">
                <ListBullets size={14} /> فهرس المحتوى
              </h4>
              <ol className="space-y-1.5 text-[13px]">
                {toc.map((t) => (
                  <li key={t.id} className={t.level === 3 ? 'pr-3' : ''}>
                    <button
                      type="button"
                      onClick={() => scrollTo(t.id)}
                      className="text-[#3D512D] hover:text-[#1F3D2B] hover:underline text-right leading-6"
                    >
                      {t.text}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>
      </div>

      {/* Lightbox */}
      {lightbox.open && lightbox.images.length > 0 && (
        <div
          className="kc-lightbox"
          role="dialog"
          aria-modal="true"
          data-testid="kc-lightbox"
          onClick={closeLightbox}
        >
          <span
            className="kc-lightbox-counter"
            data-testid="kc-lightbox-counter"
            dir="ltr"
          >
            {lightbox.index + 1} / {lightbox.images.length}
          </span>

          <button
            type="button"
            className="kc-lightbox-btn kc-lightbox-close"
            aria-label="إغلاق"
            data-testid="kc-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
          >
            <X size={20} weight="bold" />
          </button>

          {lightbox.images.length > 1 && (
            <>
              <button
                type="button"
                className="kc-lightbox-btn kc-lightbox-nav kc-lightbox-prev"
                aria-label="السابق"
                data-testid="kc-lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevLightbox();
                }}
              >
                <ArrowRight size={20} weight="bold" />
              </button>
              <button
                type="button"
                className="kc-lightbox-btn kc-lightbox-nav kc-lightbox-next"
                aria-label="التالي"
                data-testid="kc-lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextLightbox();
                }}
              >
                <ArrowLeft size={20} weight="bold" />
              </button>
            </>
          )}

          <div
            className="kc-lightbox-img-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={lightbox.images[lightbox.index]?.src}
              src={lightbox.images[lightbox.index]?.src}
              alt={lightbox.images[lightbox.index]?.alt || ''}
              className="kc-lightbox-img"
              data-testid="kc-lightbox-image"
            />
            {lightbox.images[lightbox.index]?.caption && (
              <div className="kc-lightbox-caption">
                {lightbox.images[lightbox.index].caption}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#E6E0D6] rounded-[14px] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-right px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#FAFAF7] transition-colors"
      >
        <span className="text-[15px] font-bold text-[#1F3D2B]">{q}</span>
        <CaretDown
          size={16}
          className={`text-[#4A641F] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-[#3D512D] text-sm leading-8 border-t border-[#F0EBE2]">
          {a}
        </div>
      )}
    </div>
  );
};

const ShareButton = ({ label, href, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-1.5 border border-[#E6E0D6] text-[#3D512D] hover:border-[#1F3D2B] hover:text-[#1F3D2B] rounded-full px-4 py-1.5 text-[12px] transition-colors"
  >
    <Share size={12} />
    {label}
  </a>
);

export default KnowledgeArticlePage;
